import { SUPPORT_ROLES, evaluatePrerequisite } from '../../data/supportRoles';
import { getBonusImpactExplainer } from '../../utils/bonusImpactExplainer';

/**
 * Local alias kept short for use inside _buildWelcomeMessage.
 */
const _getBonusImpact = (b) => getBonusImpactExplainer(b);

/**
 * Build a personalised welcome email body listing every concrete advantage
 * the apprenant is about to benefit from. Lazy-imports `generateId` only
 * when needed to avoid pulling helpers into the slice graph at module load.
 *
 * Format the bonus values into the user-facing units :
 *   - revenue / fuel_discount / maintenance_discount / tax_reduction /
 *     agios_reduction / turnover_reduction / event_rep_mitigation → %
 *   - reputation_passive / notoriety_passive / satisfaction_passive  → +N points
 */
const _formatBonusForEmail = (bonus) => {
  if (!bonus) return null;
  if (bonus.label) return bonus.label;
  const v = bonus.value;
  switch (bonus.type) {
    case 'revenue':                return `+${Math.round(v * 100)} % de revenus quotidiens`;
    case 'fuel_discount':          return `-${Math.round(v * 100)} % sur le carburant`;
    case 'maintenance_discount':   return `-${Math.round(v * 100)} % sur la maintenance`;
    case 'tax_reduction':          return `-${Math.round(v * 100)} % d'IS effectif`;
    case 'agios_reduction':        return `-${Math.round(v * 100)} % d'agios bancaires`;
    case 'turnover_reduction':     return `-${Math.round(v * 100)} % de turnover chauffeur`;
    case 'event_rep_mitigation':   return `-${Math.round(v * 100)} % d'impact réputation des aléas`;
    case 'reputation_passive':     return `+${v} pt de réputation tous les 30 jours`;
    case 'notoriety_passive':      return `+${v} pt de notoriété tous les 30 jours`;
    case 'satisfaction_passive':   return `+${v} pts de satisfaction chauffeurs tous les 30 jours`;
    default:                       return null;
  }
};

const _UNLOCK_LABELS = {
  market_intel:      'Veille concurrentielle sur la page Marché',
  salons:            "Bouton Salons Pro sur le Dashboard (+5 réputation pour 1 000 €)",
  grands_comptes:    'Contrats Grands Comptes à forte valeur ajoutée',
  contrats_cadres:   'Contrats-cadres fournisseurs (-5 % carburant supplémentaire)',
  crisis_comm:       'Option « Communication de crise » dans les événements',
  negociation_taux:  'Négociation bancaire : -1 % sur les nouveaux crédits',
  diag_preventif:    'Diagnostic préventif : -50 % d\'événements mécaniques aléatoires',
  fiscal_simulator:  'Simulateur fiscal prospectif dans la page Fiscalité',
  hr_dashboard:      'Tableau de bord RH avancé (satisfaction + risque turnover)',
  audit_fiscal:      "Audit fiscal automatique : détecte les optimisations manquées",
};

/**
 * Map every unlock flag to the nav path that hosts the corresponding UI.
 * Used by `hireSupport` to bump the "NEW" badge on the appropriate nav
 * link. Flags not listed here simply don't surface a badge — the welcome
 * email still lists them as an advantage so nothing is missed.
 */
const _UNLOCK_TO_NAV = {
  fiscal_simulator: '/fiscalite',
  audit_fiscal:     '/fiscalite',
  hr_dashboard:     '/hr',
  market_intel:     '/marche',
  salons:           '/',
  grands_comptes:   '/',
  contrats_cadres:  '/banque',
  crisis_comm:      '/',
  negociation_taux: '/banque',
  diag_preventif:   '/depot',
};

/**
 * Build the welcome message {subject, body} once a support role is hired.
 * Body is a multi-paragraph plain-text email that lists every concrete
 * advantage (bonus + unlocks) so the apprenant immediately understands
 * what their recruitment buys them. We push it via `pushCustomMessage`
 * so no template plumbing is needed.
 */
const _buildWelcomeMessage = (role) => {
  const advantages = (role.bonuses || []).map(_formatBonusForEmail).filter(Boolean);
  const unlocks = (role.unlocks || [])
    .map(flag => _UNLOCK_LABELS[flag] || flag)
    .filter(Boolean);
  const lines = [];
  lines.push(`Bonjour,`);
  lines.push('');
  lines.push(`Je me présente : ${role.name}, ${role.role} (${role.level}).`);
  if (role.background) {
    lines.push('');
    lines.push(role.background);
  }
  lines.push('');
  lines.push(`Mon rôle au quotidien va vous apporter les avantages suivants :`);
  if (advantages.length === 0 && unlocks.length === 0) {
    lines.push('• Soutien général de l\'équipe.');
  }
  advantages.forEach(a => lines.push(`• ${a}`));
  unlocks.forEach(u => lines.push(`• ${u}`));

  // 03/06/2026 — Section "Impact métier concret" : chaque bonus est
  // traduit en KPI business observable (marge brute, TCO, BFR, etc.).
  if ((role.bonuses || []).length > 0) {
    lines.push('');
    lines.push('━━━ IMPACT MÉTIER CONCRET ━━━');
    (role.bonuses || []).forEach(b => {
      const impact = _getBonusImpact(b);
      lines.push('');
      lines.push(`▸ ${impact.kpi} — ${impact.detail}`);
    });
  }

  lines.push('');
  lines.push(`Coût mensuel à prévoir : ${role.monthlyCost} € (charges incluses).`);
  lines.push('');
  lines.push(`Je prends mes fonctions dès aujourd'hui — n'hésitez pas à venir me voir si vous voulez creuser un sujet.`);
  lines.push('');
  lines.push(`Cordialement,`);
  lines.push(`${role.name}`);
  return {
    subject: `Bienvenue à bord — ${role.role}`,
    body: lines.join('\n'),
  };
};

/**
 * Sum the `value` of every bonus matching `type` across all hired roles.
 */
const sumBonus = (hired, type) =>
  Object.keys(hired || {}).reduce((acc, roleId) => {
    const role = SUPPORT_ROLES.find(r => r.id === roleId);
    if (!role) return acc;
    return acc + (role.bonuses || [])
      .filter(b => b.type === type)
      .reduce((s, b) => s + b.value, 0);
  }, 0);

/**
 * For mitigation bonuses (e.g. -50 % impact), the highest single value wins
 * rather than stacking (to keep the cap below 100 %).
 */
const maxBonus = (hired, type) =>
  Object.keys(hired || {}).reduce((acc, roleId) => {
    const role = SUPPORT_ROLES.find(r => r.id === roleId);
    if (!role) return acc;
    return (role.bonuses || [])
      .filter(b => b.type === type)
      .reduce((m, b) => Math.max(m, b.value), acc);
  }, 0);

export const createSupportSlice = (set, get) => ({
  hiredSupport: {},
  // 02/06/2026 — Track the most recent hire so a celebration popup can
  // show the bonuses just unlocked. Set on hireSupport, cleared by the
  // popup itself via clearJustHiredSupport.
  justHiredSupport: null,

  clearJustHiredSupport: () => set({ justHiredSupport: null }),

  hireSupport: (roleId) => {
    const state = get();
    const role = SUPPORT_ROLES.find(r => r.id === roleId);
    if (!role) return { ok: false, reason: 'unknown_role' };
    if (state.hiredSupport[roleId]) return { ok: false, reason: 'already_hired' };
    const check = evaluatePrerequisite(role, state);
    if (!check.ok) return { ok: false, reason: 'prerequisite_unmet', label: check.label };

    // 18/06/2026 — demande user : après une démission pour absence de
    // siège social, impossible de réembaucher une 3e fonction support
    // tant que l'investissement « Siège social » n'est pas réalisé.
    const hqActive = !!(state.activeInvestments || {})['inv-office-01'];
    const currentCount = Object.keys(state.hiredSupport || {}).length;
    if (!hqActive && state.hqResignationHappened && currentCount >= 2) {
      return { ok: false, reason: 'hq_required' };
    }

    const nextHired = {
      ...state.hiredSupport,
      [roleId]: { hiredAt: state.totalDays || 0 },
    };
    const hiredCount = Object.keys(nextHired).length;
    const patch = {
      hiredSupport: nextHired,
      // 02/06/2026 — Flag the new hire so the celebration popup picks it up.
      justHiredSupport: { roleId, hiredAt: state.totalDays || 0 },
    };

    // 18/06/2026 — 3e fonction support sans siège investi : armement du
    // compte à rebours de 45 jours (annonce immédiate en Messagerie).
    let armedHqDeadline = false;
    if (hiredCount >= 3 && !hqActive && state.hqDeadlineDay == null) {
      patch.hqDeadlineDay = (state.totalDays || 0) + 45;
      patch.hqRemindersSent = {};
      armedHqDeadline = true;
    }

    // Team cohesion milestone: hitting 5 hired roles for the first time grants
    // +5 reputation + +5 satisfaction across all drivers (one-shot).
    if (hiredCount >= 5 && !state.cohesionUnlocked) {
      patch.cohesionUnlocked = true;
      patch.reputation = Math.min(100, (state.reputation || 0) + 5);
      patch.drivers = (state.drivers || []).map(d => ({
        ...d,
        satisfaction: Math.min(100, (d.satisfaction || 0) + 5),
      }));
      patch.notifications = [
        ...(state.notifications || []).slice(-30),
        {
          id: `cohesion-${Date.now()}`,
          type: 'team_cohesion',
          title: "Cohésion d'équipe atteinte !",
          message: '5 fonctions support recrutées — réputation +5, satisfaction des chauffeurs +5.',
          day: state.totalDays || 0,
          read: false,
        },
      ];
    }

    set(patch);

    // 18/06/2026 — annonce du délai de 45 jours pour le siège social.
    if (armedHqDeadline && typeof get()._pushHqDeadlineAnnouncement === 'function') {
      get()._pushHqDeadlineAnnouncement(patch.hqDeadlineDay);
    }

    // 09/02/2026 — Q2 Lot H user prod : Nadia Cheikh / Karim Sefrioui
    // apportent dans leur escarcelle 4-5 profils chauffeurs au vivier.
    // Variété + au moins 1 profil > Tier max flotte actuelle. Karim
    // marque 2 profils comme pris par un concurrent (débauchables).
    try {
      const { buildSupportDriverBoostPatch, buildSupportDriverBoostMessage } = require('../helpers/supportDriverBoost');
      const boost = buildSupportDriverBoostPatch(roleId, get());
      if (boost) {
        set({ driverPool: boost.driverPool });
        const msg = buildSupportDriverBoostMessage(boost);
        if (msg && typeof get().pushCustomMessage === 'function') {
          get().pushCustomMessage(msg);
        }
      }
    } catch (err) { console.warn('[support] driver pool boost failed:', err); }

    // Push a welcome message into the Messagerie so the apprenant immediately
    // sees a personalised email from the new hire summarising all the
    // advantages they're about to get. Wrapped in try/catch so an inbox bug
    // never blocks the hire mutation.
    try {
      const { subject, body } = _buildWelcomeMessage(role);
      const push = get().pushCustomMessage;
      if (typeof push === 'function') {
        push({
          senderType: 'support',
          senderName: role.name,
          tone: 'friendly',
          kind: 'info',
          subject,
          body,
          ttlDays: 14,
          payload: { roleId: role.id, kind: 'support_welcome' },
        });
      }
    } catch (err) { console.warn('[support] welcome message push failed:', err); }

    // Bump the "NEW" badge on the nav links that host the newly unlocked
    // features so the apprenant immediately spots where to go. Wrapped in
    // a try/catch — if the core slice is somehow missing the action we
    // don't want to corrupt the hire flow.
    try {
      const bump = get().bumpNewFeatureBadge;
      if (typeof bump === 'function') {
        (role.unlocks || []).forEach((flag) => {
          const path = _UNLOCK_TO_NAV[flag];
          if (path) bump(path);
        });
      }
    } catch (err) { console.warn('[support] badge bump failed:', err); }

    return { ok: true, cohesionUnlocked: !state.cohesionUnlocked && hiredCount >= 5 };
  },

  fireSupport: (roleId) => {
    const state = get();
    if (!state.hiredSupport[roleId]) return { ok: false, reason: 'not_hired' };
    const next = { ...state.hiredSupport };
    delete next[roleId];
    set({ hiredSupport: next });
    return { ok: true };
  },

  // ---- aggregators consumed by core / event / tax / bank / hr slices ----
  getSupportRevenueMultiplier: () => sumBonus(get().hiredSupport, 'revenue'),
  // Fuel discount = sum of role bonuses + 5% extra when the contrats_cadres
  // unlock is active (Hélène Pichon Senior Achats).
  getSupportFuelDiscount: () => {
    const base = sumBonus(get().hiredSupport, 'fuel_discount');
    const cadresBonus = (() => {
      const hired = get().hiredSupport || {};
      return Object.keys(hired).some(roleId => {
        const role = SUPPORT_ROLES.find(r => r.id === roleId);
        return role && (role.unlocks || []).includes('contrats_cadres');
      }) ? 0.05 : 0;
    })();
    return base + cadresBonus;
  },
  getSupportMaintenanceDiscount: () => sumBonus(get().hiredSupport, 'maintenance_discount'),
  getSupportTaxReduction:     () => sumBonus(get().hiredSupport, 'tax_reduction'),
  getSupportAgiosReduction:   () => sumBonus(get().hiredSupport, 'agios_reduction'),
  getSupportReputationPassive:() => sumBonus(get().hiredSupport, 'reputation_passive'),
  getSupportNotorietyPassive: () => sumBonus(get().hiredSupport, 'notoriety_passive'),
  getSupportSatisfactionPassive: () => sumBonus(get().hiredSupport, 'satisfaction_passive'),
  getSupportTurnoverReduction: () => maxBonus(get().hiredSupport, 'turnover_reduction'),
  getSupportEventRepMitigation: () => maxBonus(get().hiredSupport, 'event_rep_mitigation'),

  getSupportMonthlyCost: () =>
    Object.keys(get().hiredSupport || {}).reduce((sum, roleId) => {
      const role = SUPPORT_ROLES.find(r => r.id === roleId);
      return sum + (role ? role.monthlyCost : 0);
    }, 0),

  hasSupportUnlock: (flag) => {
    const { hiredSupport = {} } = get();
    return Object.keys(hiredSupport).some(roleId => {
      const role = SUPPORT_ROLES.find(r => r.id === roleId);
      return role && (role.unlocks || []).includes(flag);
    });
  },
});
